import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['item', 'icon', 'panel', 'caption', 'stepUp', 'stepDown', 'title', 'desc']

  connect () {
    this.activeIndex = 0
    this.refresh()
    this.onResize = () => this.scheduleResize()
    window.addEventListener('resize', this.onResize)
    document.fonts.ready.then(this.onResize)
  }

  disconnect () {
    window.removeEventListener('resize', this.onResize)
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame)
  }

  scheduleResize () {
    if (this.resizeFrame) return
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null
      this.refresh()
    })
  }

  refresh () {
    this.collapsedChrome = this.measureChrome()
    this.render()
    this.setWidths(false)
    this.reserveHeight()
  }

  measureChrome () {
    const item = this.itemTargets[0]
    const button = item?.querySelector('button')
    if (!item || !button) return 78
    const pad = getComputedStyle(item)
    const gap = parseFloat(getComputedStyle(button).columnGap) || 0
    const padX = parseFloat(pad.paddingLeft) + parseFloat(pad.paddingRight)
    const iconWidth = this.iconTargets.find(icon => icon.offsetWidth)?.offsetWidth || 24
    return Math.ceil(padX + iconWidth + gap) + 2
  }

  select (event) {
    this.setActive(Number(event.currentTarget.dataset.index))
  }

  stepUp () {
    this.setActive(this.activeIndex - 1)
  }

  stepDown () {
    this.setActive(this.activeIndex + 1)
  }

  setActive (index) {
    const max = this.itemTargets.length - 1
    this.activeIndex = Math.max(0, Math.min(index, max))
    this.render()
    this.setWidths(true)
  }

  setWidths (animated) {
    const list = this.itemTargets[0]?.parentElement
    if (!list) return

    const fullWidth = list.getBoundingClientRect().width
    const widths = this.itemTargets.map((item, i) => (
      i === this.activeIndex
        ? fullWidth
        : Math.ceil(item.querySelector('span.whitespace-nowrap').getBoundingClientRect().width) + this.collapsedChrome
    ))

    this.itemTargets.forEach((item, i) => {
      if (!animated) item.style.transition = 'none'
      item.style.width = `${widths[i]}px`
    })

    if (!animated) {
      list.getBoundingClientRect()
      this.itemTargets.forEach(item => { item.style.transition = '' })
    }
  }

  reserveHeight () {
    const list = this.itemTargets[0]?.parentElement
    if (!list) return

    list.style.minHeight = ''
    const fullWidth = list.getBoundingClientRect().width
    if (!fullWidth) return

    const pad = getComputedStyle(this.itemTargets[0])
    const padX = parseFloat(pad.paddingLeft) + parseFloat(pad.paddingRight)
    this.descTargets.forEach(desc => { desc.style.width = `${fullWidth - padX}px` })

    this.panelTargets.forEach(panel => { panel.style.transition = 'none' })
    const descHeights = this.descTargets.map(desc => desc.getBoundingClientRect().height)
    const settled = list.getBoundingClientRect().height
    list.style.minHeight = `${Math.ceil(settled - descHeights[this.activeIndex] + Math.max(...descHeights))}px`
    list.getBoundingClientRect()
    this.panelTargets.forEach(panel => { panel.style.transition = '' })
  }

  render () {
    this.panelTargets.forEach((panel, i) => {
      const open = i === this.activeIndex
      panel.classList.toggle('grid-rows-[1fr]', open)
      panel.classList.toggle('grid-rows-[0fr]', !open)
    })

    this.titleTargets.forEach((title, i) => {
      const open = i === this.activeIndex
      title.classList.toggle('pb-1', open)
      title.classList.toggle('pb-3', !open)
      title.setAttribute('aria-expanded', open)
    })

    this.iconTargets.forEach((icon, i) => {
      icon.classList.toggle('hidden', i === this.activeIndex)
    })

    this.itemTargets.forEach((item, i) => {
      const active = i === this.activeIndex
      item.classList.toggle('!bg-indigo-50', active)
      item.classList.toggle('ring-1', active)
      item.classList.toggle('ring-indigo-200', active)
    })

    this.captionTargets.forEach((caption, i) => {
      const active = i === this.activeIndex
      caption.classList.toggle('opacity-100', active)
      caption.classList.toggle('scale-100', active)
      caption.classList.toggle('opacity-0', !active)
      caption.classList.toggle('scale-105', !active)
      caption.classList.toggle('pointer-events-none', !active)
      caption.classList.toggle('ai-shot-active', active)
    })

    if (this.hasStepUpTarget) this.stepUpTarget.disabled = this.activeIndex === 0
    if (this.hasStepDownTarget) this.stepDownTarget.disabled = this.activeIndex === this.itemTargets.length - 1
  }
}
